import { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot, updateDoc, collection, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import AgentCallScreen from './AgentCallScreen';

export default function AgentDashboard({ agentId, onLogout }) {
  const [agent, setAgent] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [incomingCall, setIncomingCall] = useState(null);
  const [acceptedCall, setAcceptedCall] = useState(null);
  const ringIntervalRef = useRef(null);
  

  useEffect(() => {
    if (!agentId) return;
    const unsubscribe = onSnapshot(doc(db, 'agents', agentId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setAgent(data);
        setIsOnline(data.isOnline || false);
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [agentId]);

  useEffect(() => {
    if (!agentId) return;
    const thirtySecsAgo = new Date(Date.now() - 30000);
    const q = query(
      collection(db, 'sessions'),
      where('agentId', '==', agentId),
      where('status', '==', 'calling'),
      where('startTime', '>=', thirtySecsAgo)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added') {
          const session = { id: change.doc.id, ...change.doc.data() };
          setIncomingCall(session);
          startRinging();
        }
      });
    });
    return unsubscribe;
  }, [agentId]);

  const startRinging = () => {
    stopRinging();
    const ring = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.frequency.value = 880;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.8);
      } catch (e) {}
    };
    ring();
    ringIntervalRef.current = setInterval(ring, 1500);
  };

  const stopRinging = () => {
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
  };

  const toggleOnline = async () => {
    const newStatus = !isOnline;
    setIsOnline(newStatus);
    await updateDoc(doc(db, 'agents', agentId), { isOnline: newStatus });
  };

  const toRupees = (coins) => ((coins || 0) * 0.0556).toFixed(0);

  const declineCall = async () => {
    stopRinging();
    await updateDoc(doc(db, 'sessions', incomingCall.id), { status: 'declined' });
    setIncomingCall(null);
  };

  const acceptCall = () => {
    stopRinging();
    setAcceptedCall(incomingCall);
    setIncomingCall(null);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading dashboard...</div>
      </div>
    );
  }

  if (incomingCall) {
    return (
      <div style={styles.incomingContainer}>
        <div style={styles.incomingCard}>
          <div style={styles.incomingPulse}>📞</div>
          <div style={styles.incomingTitle}>Incoming Call!</div>
          <div style={styles.incomingSub}>Anonymous customer wants to practice German</div>
          <div style={styles.incomingBtns}>
            <button style={styles.declineBtn} onClick={declineCall}>Decline</button>
            <button style={styles.acceptBtn} onClick={acceptCall}>Accept ✓</button>
          </div>
        </div>
      </div>
    );
  }

  if (acceptedCall) {
    return (
      <AgentCallScreen
        channelName={acceptedCall.channelName}
        sessionId={acceptedCall.id}
        agentId={agentId}
        onEnd={() => setAcceptedCall(null)}
      />
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.logoText}>
            <span style={styles.logoDe}>de</span>
            <span style={styles.logoLrn}>lrn</span>
          </div>
          <div style={styles.headerSub}>Agent Dashboard</div>
        </div>
        <button style={styles.logoutBtn} onClick={onLogout}>⏻</button>
      </div>

      {/* Online Toggle */}
      <div style={{
        ...styles.toggleCard,
        ...(isOnline ? styles.toggleCardOnline : {})
      }} onClick={toggleOnline}>
        <div>
          <div style={styles.toggleTitle}>
            {isOnline ? '🟢 You are ONLINE' : '⚫ You are OFFLINE'}
          </div>
          <div style={styles.toggleSub}>
            {isOnline ? 'Customers can call you now' : 'Tap to start earning'}
          </div>
        </div>
        <div style={{
          ...styles.toggle,
          backgroundColor: isOnline ? '#00d4a0' : '#ffffff20',
        }}>
          <div style={{
            ...styles.toggleThumb,
            transform: isOnline ? 'translateX(24px)' : 'translateX(2px)',
          }}></div>
        </div>
      </div>

      {/* Agent Info */}
      <div style={styles.infoCard}>
        <div style={styles.agentRow}>
          <div style={styles.agentAvatar}>{agent?.avatar || '🎓'}</div>
          <div style={styles.agentDetails}>
            <div style={styles.agentName}>{agent?.codename}</div>
            <div style={styles.agentSpec}>{agent?.specialty}</div>
            <div style={styles.agentMeta}>
              🪙 {agent?.ratePerSecond}/sec · ⭐ {agent?.rating?.toFixed(1)}
            </div>
          </div>
        </div>
      </div>

      {/* Earnings */}
      <div style={styles.earningsCard}>
        <div style={styles.earningsHeader}>
          <span style={styles.cardTitle}>💰 Earnings</span>
          <span style={styles.sessionCount}>{agent?.totalSessions || 0} sessions</span>
        </div>
        <div style={styles.earningsRow}>
          <div style={styles.earningItem}>
            <div style={styles.earningValue}>₹{toRupees(agent?.pendingWithdrawal)}</div>
            <div style={styles.earningLabel}>Pending</div>
          </div>
          <div style={styles.earningDivider}></div>
          <div style={styles.earningItem}>
            <div style={styles.earningValue}>₹{toRupees(agent?.totalEarnings)}</div>
            <div style={styles.earningLabel}>Total Earned</div>
          </div>
        </div>
      </div>

      {/* Withdraw Button */}
      <button style={styles.withdrawBtn}>
        💸 Request Withdrawal
      </button>

      {/* Rules */}
      <div style={styles.rulesCard}>
        <div style={styles.rulesTitle}>📋 Guidelines</div>
        {[
          'Stay anonymous — never share real name',
          'Speak only German during sessions',
          'Use Emergency Stop if customer misbehaves',
          'Minimum ₹200 for withdrawal',
          'Payout within 48 hours via UPI',
        ].map((rule, i) => (
          <div key={i} style={styles.rule}>
            <span style={styles.ruleDot}>→</span>
            <span style={styles.ruleText}>{rule}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: { backgroundColor: '#0a1628', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" },
  loading: { color: '#ffffff50', textAlign: 'center', padding: '40px', fontSize: '16px' },
  header: { padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ffffff10', background: 'linear-gradient(180deg, #0d2137, #0a1628)' },
  logoText: { fontSize: '24px', fontWeight: '900', lineHeight: 1 },
  logoDe: { color: '#ffffff' },
  logoLrn: { color: '#00d4a0' },
  headerSub: { color: '#ffffff40', fontSize: '11px', marginTop: '2px' },
  logoutBtn: { backgroundColor: '#ffffff10', border: 'none', borderRadius: '10px', padding: '8px 10px', color: '#ffffff60', cursor: 'pointer', fontSize: '16px' },
  toggleCard: { margin: '16px', backgroundColor: '#0d2137', borderRadius: '20px', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #ffffff10', cursor: 'pointer' },
  toggleCardOnline: { borderColor: '#00d4a050', backgroundColor: '#00d4a010' },
  toggleTitle: { color: '#ffffff', fontWeight: '800', fontSize: '16px', marginBottom: '4px' },
  toggleSub: { color: '#ffffff60', fontSize: '13px' },
  toggle: { width: '52px', height: '28px', borderRadius: '14px', position: 'relative', transition: 'background-color 0.3s', flexShrink: 0 },
  toggleThumb: { position: 'absolute', top: '2px', width: '24px', height: '24px', borderRadius: '12px', backgroundColor: '#ffffff', transition: 'transform 0.3s' },
  infoCard: { margin: '0 16px 16px', backgroundColor: '#0d2137', borderRadius: '20px', padding: '16px', border: '1px solid #ffffff10' },
  agentRow: { display: 'flex', gap: '14px', alignItems: 'center' },
  agentAvatar: { width: '56px', height: '56px', borderRadius: '16px', backgroundColor: '#0a1628', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', flexShrink: 0, border: '2px solid #00d4a030' },
  agentDetails: {},
  agentName: { color: '#ffffff', fontWeight: '800', fontSize: '16px' },
  agentSpec: { color: '#00d4a0', fontSize: '13px', fontWeight: '600', marginTop: '2px' },
  agentMeta: { color: '#ffffff50', fontSize: '12px', marginTop: '4px' },
  earningsCard: { margin: '0 16px 16px', backgroundColor: '#0d2137', borderRadius: '20px', padding: '16px', border: '1px solid #ffffff10' },
  earningsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  cardTitle: { color: '#ffffff', fontWeight: '800', fontSize: '15px' },
  sessionCount: { color: '#00d4a0', fontSize: '12px', fontWeight: '700' },
  earningsRow: { display: 'flex' },
  earningItem: { flex: 1, textAlign: 'center' },
  earningValue: { color: '#00d4a0', fontWeight: '900', fontSize: '24px' },
  earningLabel: { color: '#ffffff50', fontSize: '11px', marginTop: '4px' },
  earningDivider: { width: '1px', backgroundColor: '#ffffff10' },
  withdrawBtn: { margin: '0 16px 16px', width: 'calc(100% - 32px)', background: 'linear-gradient(135deg, #00d4a0, #00a87d)', border: 'none', borderRadius: '14px', padding: '16px', color: '#0a1628', fontWeight: '800', fontSize: '15px', cursor: 'pointer' },
  rulesCard: { margin: '0 16px', backgroundColor: '#0d2137', borderRadius: '20px', padding: '16px', border: '1px solid #ffffff10' },
  rulesTitle: { color: '#ffffff', fontWeight: '800', fontSize: '14px', marginBottom: '12px' },
  rule: { display: 'flex', gap: '8px', padding: '6px 0', borderBottom: '1px solid #ffffff06' },
  ruleDot: { color: '#00d4a0' },
  ruleText: { color: '#ffffff70', fontSize: '13px' },
  incomingContainer: { backgroundColor: '#0a1628', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Segoe UI', sans-serif" },
  incomingCard: { backgroundColor: '#0d2137', borderRadius: '24px', padding: '40px', textAlign: 'center', width: '100%', maxWidth: '360px', border: '1px solid #00d4a030' },
  incomingPulse: { fontSize: '72px', marginBottom: '16px' },
  incomingTitle: { color: '#ffffff', fontWeight: '900', fontSize: '22px', marginBottom: '8px' },
  incomingSub: { color: '#ffffff60', fontSize: '14px', marginBottom: '32px' },
  incomingBtns: { display: 'flex', gap: '12px' },
  declineBtn: { flex: 1, backgroundColor: '#ffffff10', border: 'none', borderRadius: '14px', padding: '16px', color: '#ffffff80', fontWeight: '700', fontSize: '16px', cursor: 'pointer' },
  acceptBtn: { flex: 2, background: 'linear-gradient(135deg, #00d4a0, #00a87d)', border: 'none', borderRadius: '14px', padding: '16px', color: '#0a1628', fontWeight: '800', fontSize: '18px', cursor: 'pointer' },
};